const { MinimogThemeStyles, MinimogThemeScripts } = window;

class QuickView {
  constructor() {
    this.modal = new MinimogTheme.Modal();
    this.isOpen = false;
    this.isLoading = false;
    this.refreshTimers = [];

    addEventDelegate({
      selector: ".m-product-quickview-button",
      handler: (e, target) => {
        e.preventDefault();
        if (this.isLoading) return;

        this.target = target;
        this.toggleLoading(true);

        const productHandle = target.dataset.productHandle;
        if (productHandle) this.loadProductQuickView(productHandle);
      },
    });

    window.MinimogEvents.subscribe(MinimogTheme.pubSubEvents.cartUpdate, () => {
      if (this.modal) this.modal.close();
    });

    window.MinimogEvents.subscribe("ON_MODAL_CLOSED", () => {
      this.close();
    });
  }

  loadProductQuickView(productHandle) {
    if (this.isOpen) {
      this.modal && this.modal.close();
    }

    this.clearRefreshTimers();
    this.isLoading = true;

    fetchSection("product-quickview", { url: `${window.MinimogSettings.base_url}products/${productHandle}` })
      .then((html) => {
        this.modalContent = html.querySelector("#MainProduct-quick-view__content");
        const productInfo = html.querySelector("product-info");
        const colorScheme = productInfo ? productInfo.dataset.colorScheme : "";

        if (!this.modalContent) throw new Error("Quick view content not found");

        this.cleanupModalClasses();

        this.modal.appendChild(this.modalContent);

        const qvWidth = this.modalContent.dataset.auraQvWidth || "1120px";
        const qvModeOverride = this.target && this.target.dataset ? this.target.dataset.auraQvModeOverride : "";
        const qvMode = qvModeOverride || this.modalContent.dataset.auraQvMode || "hybrid";
        this.applyRuntimeMode(qvMode);
        const isBottomSheet = this.modalContent.dataset.auraQvMobileSheet !== "false";
        const overlayOpacity = this.modalContent.style.getPropertyValue("--aura-qv-overlay-opacity") || ".45";
        const radius = this.modalContent.style.getPropertyValue("--aura-qv-radius") || "0px";
        const bg = this.modalContent.style.getPropertyValue("--aura-qv-bg") || "#fff";

        this.modal.setWidth(qvWidth);

        if (this.modal.modal) {
          this.modal.modal.classList.add("aura-qv-modal", `aura-qv-modal--${qvMode}`);
          this.modal.modal.classList.toggle("aura-qv-modal--mobile-sheet", isBottomSheet);
          this.modal.modal.style.setProperty("--aura-qv-overlay-opacity", overlayOpacity.trim());
          this.modal.modal.style.setProperty("--aura-qv-radius", radius.trim());
          this.modal.modal.style.setProperty("--aura-qv-bg", bg.trim());
        }

        const sectionWrapper = this.modal.modal.querySelector("#MainProduct-quick-view__content");

        sectionWrapper.querySelectorAll("script").forEach((oldScriptTag) => {
          const newScriptTag = document.createElement("script");
          Array.from(oldScriptTag.attributes).forEach((attribute) => {
            newScriptTag.setAttribute(attribute.name, attribute.value);
          });
          newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
          oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
        });

        document.documentElement.classList.add("aura-qv-is-open");
        this.modal.open();
        this.modal.setSizes(`m-gradient ${colorScheme}`);
        this.bindMediaRefreshEvents();
        this.scheduleMediaGalleryRefresh();

        this.toggleLoading(false);
        this.isOpen = true;
        this.isLoading = false;
      })
      .then(() => {
        const activeProductHandle = this.target && this.target.dataset ? this.target.dataset.productHandle : productHandle;

        document.dispatchEvent(
          new CustomEvent("tcustomizer-custom-show-quick-view", {
            detail: { productHandle: activeProductHandle },
          })
        );

        document.dispatchEvent(
          new CustomEvent("quick-view:loaded", {
            detail: { productUrl: activeProductHandle },
          })
        );

        this.scheduleMediaGalleryRefresh();
      })
      .catch((error) => {
        console.error("Error loading product quick view:", error);
        this.toggleLoading(false);
        this.clearRefreshTimers();
        this.isLoading = false;
      });
  }

  getQuickViewRoot() {
    if (this.modal && this.modal.modal) {
      return this.modal.modal.querySelector("#MainProduct-quick-view__content") || this.modal.modal;
    }

    return this.modalContent || null;
  }

  bindMediaRefreshEvents() {
    const root = this.getQuickViewRoot();
    if (!root) return;

    root.querySelectorAll('link[rel="stylesheet"]').forEach((link) => {
      if (link.dataset.auraQvRefreshBound === "true") return;
      link.dataset.auraQvRefreshBound = "true";
      link.addEventListener("load", () => this.scheduleMediaGalleryRefresh(), { once: true });
    });

    root.querySelectorAll(".aura-qv__media img").forEach((image) => {
      if (image.dataset.auraQvRefreshBound === "true") return;
      image.dataset.auraQvRefreshBound = "true";

      if (!image.complete) {
        image.addEventListener("load", () => this.scheduleMediaGalleryRefresh(), { once: true });
      }
    });
  }

  scheduleMediaGalleryRefresh() {
    this.clearRefreshTimers();

    const refresh = () => this.refreshMediaGallery();

    if (window.requestAnimationFrame) {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(refresh);
      });
    }

    [80, 180, 360, 700, 1100].forEach((delay) => {
      this.refreshTimers.push(window.setTimeout(refresh, delay));
    });
  }

  clearRefreshTimers() {
    if (!this.refreshTimers || !this.refreshTimers.length) return;

    this.refreshTimers.forEach((timer) => window.clearTimeout(timer));
    this.refreshTimers = [];
  }

  refreshMediaGallery() {
    const root = this.getQuickViewRoot();
    if (!root) return;

    const swiperSelectors = [
      ".aura-qv__media .swiper",
      ".aura-qv__media .swiper-container",
      ".aura-qv__media .nav-swiper-container",
    ];

    const updateSwiper = (swiper, resetMainSlide = false) => {
      if (!swiper || typeof swiper.update !== "function") return;

      try {
        if (typeof swiper.updateSize === "function") swiper.updateSize();
        if (typeof swiper.updateSlides === "function") swiper.updateSlides();
        swiper.update();

        if (resetMainSlide && typeof swiper.slideTo === "function") {
          swiper.slideTo(swiper.activeIndex || 0, 0, false);
        }
      } catch (error) {
        console.warn("Unable to refresh quick view slider", error);
      }
    };

    root.querySelectorAll(swiperSelectors.join(",")).forEach((slider) => {
      const isMainMediaSlider = !!slider.closest(".m-media-gallery__inner");
      updateSwiper(slider.swiper, isMainMediaSlider);
    });

    const gallery = root.querySelector("media-gallery");
    if (gallery) {
      ["swiper", "slider", "mainSlider", "mediaSlider", "navSlider", "thumbnailSlider"].forEach((key) => {
        updateSwiper(gallery[key], key !== "navSlider" && key !== "thumbnailSlider");
      });
    }

    window.dispatchEvent(new Event("resize"));
  }

  applyRuntimeMode(mode) {
    if (!this.modalContent || !mode) return;

    const root = this.modalContent.querySelector(".aura-qv");
    if (root) {
      root.classList.remove("aura-qv--mode-catalogue", "aura-qv--mode-ecommerce", "aura-qv--mode-hybrid");
      root.classList.add(`aura-qv--mode-${mode}`);
      root.setAttribute("data-aura-runtime-mode", mode);
    }

    this.modalContent.classList.remove(
      "aura-qv-content--mode-catalogue",
      "aura-qv-content--mode-ecommerce",
      "aura-qv-content--mode-hybrid"
    );
    this.modalContent.classList.add(`aura-qv-content--mode-${mode}`);
    this.modalContent.dataset.auraQvMode = mode;
  }

  cleanupModalClasses() {
    if (!this.modal || !this.modal.modal) return;

    this.modal.modal.classList.remove(
      "aura-qv-modal",
      "aura-qv-modal--catalogue",
      "aura-qv-modal--ecommerce",
      "aura-qv-modal--hybrid",
      "aura-qv-modal--mobile-sheet"
    );

    this.modal.modal.style.removeProperty("--aura-qv-overlay-opacity");
    this.modal.modal.style.removeProperty("--aura-qv-radius");
    this.modal.modal.style.removeProperty("--aura-qv-bg");
  }

  close() {
    this.isOpen = false;
    document.documentElement.classList.remove("aura-qv-is-open");
    this.clearRefreshTimers();
    this.cleanupModalClasses();
  }

  toggleLoading(isLoading) {
    if (!this.target) return;

    if (isLoading) {
      this.target.classList.add("m-spinner-loading");
    } else {
      this.target.classList.remove("m-spinner-loading");
    }
  }
}

MinimogTheme.ProductQuickView = new QuickView();
